import React from 'react';
import { Heart, Truck, ShoppingCart } from 'lucide-react';

const industriesData = {
  title: "Domain-Specific Intelligence",
  tagline: "Built-in expertise, not generic customization",
  industries: [
    {
      name: "Healthcare",
      description: "HIPAA-compliant agents that understand patient management, clinical workflows, and care coordination",
      icon: "Heart",
      features: [
        "Patient record management with privacy protection",
        "Appointment scheduling and care coordination", 
        "Clinical workflow automation and optimization",
        "Insurance claims processing and verification",
        "Regulatory compliance monitoring and reporting"
      ],
      compliance: "HIPAA-compliant by design"
    },
    {
      name: "Logistics", 
      description: "Supply chain intelligence with real-time tracking, vendor management, and automated notifications",
      icon: "Truck",
      features: [
        "Real-time shipment tracking and updates",
        "Vendor relationship and performance management",
        "Inventory optimization and demand forecasting", 
        "Route planning and delivery coordination",
        "Automated alerts and exception handling"
      ],
      compliance: "SOX and trade regulation ready"
    },
    {
      name: "E-commerce",
      description: "Customer journey optimization with inventory sync, payment processing, and conversion intelligence", 
      icon: "ShoppingCart",
      features: [
        "Customer behavior analysis and personalization",
        "Real-time inventory synchronization across channels",
        "Multi-gateway payment processing and fraud detection",
        "Conversion optimization and abandoned cart recovery",
        "Order fulfillment and customer service automation"
      ],
      compliance: "PCI-DSS and GDPR compliant"
    }
  ]
};

const iconMap = {
  Heart: Heart,
  Truck: Truck, 
  ShoppingCart: ShoppingCart
};

const Industries = () => {
  return (
    <section id="industries" className="dark-full-container" style={{ paddingTop: '160px', paddingBottom: '160px' }}>
      <div className="dark-content-container">
        <div className="reveal" style={{ textAlign: 'center', marginBottom: '100px' }}>
          <h2 className="display-large" style={{ marginBottom: '24px', color: 'var(--brand-primary)' }}>
            {industriesData.title}
          </h2>
          <p className="body-large" style={{ maxWidth: '800px', margin: '0 auto 32px auto' }}>
            Deep industry knowledge embedded in every AI agent. We don't just customize—we understand your sector's unique challenges, regulations, and workflows.
          </p>
          <p className="heading-2" style={{ color: 'var(--brand-primary)', fontStyle: 'italic' }}>
            {industriesData.tagline}
          </p>
        </div>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '40px',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {industriesData.industries.map((industry, index) => {
            const IconComponent = iconMap[industry.icon];
            return (
              <div 
                key={index}
                className="ui-card reveal"
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-subtle)',
                  padding: '50px',
                  transitionDelay: `${index * 0.12}s`
                }}
              >
                <div style={{ marginBottom: '32px' }}>
                  <IconComponent 
                    size={48} 
                    color="var(--brand-primary)" 
                    className="icon-badge"
                    style={{ marginBottom: '24px' }}
                  />
                  
                  <h3 className="heading-1" style={{ marginBottom: '16px' }}>
                    {industry.name}
                  </h3>
                  
                  <p className="body-medium" style={{ 
                    marginBottom: '24px',
                    lineHeight: 1.6,
                    color: 'var(--text-secondary)'
                  }}>
                    {industry.description}
                  </p>
                  
                  <div style={{
                    padding: '12px 16px',
                    backgroundColor: 'var(--brand-hover)',
                    border: '1px solid var(--brand-primary)',
                    borderRadius: '6px',
                    marginBottom: '32px'
                  }}>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'var(--brand-primary)'
                    }}>
                      {industry.compliance}
                    </span>
                  </div>
                </div>
                
                <div>
                  <h4 className="heading-3" style={{ 
                    marginBottom: '20px',
                    color: 'var(--text-primary)'
                  }}>
                    Key Capabilities:
                  </h4>
                  
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {industry.features.map((feature, featureIndex) => (
                      <li 
                        key={featureIndex} 
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '12px',
                          marginBottom: '12px',
                          fontSize: '16px',
                          color: 'var(--text-secondary)',
                          lineHeight: 1.5
                        }}
                      >
                        <div style={{
                          width: '6px',
                          height: '6px',
                          backgroundColor: 'var(--brand-primary)',
                          borderRadius: '50%',
                          marginTop: '8px',
                          flexShrink: 0
                        }} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Bottom CTA */}
        <div className="reveal" style={{
          textAlign: 'center',
          marginTop: '80px',
          padding: '50px',
          background: 'var(--bg-primary)',
          border: '1px solid var(--border-subtle)',
          borderRadius: '6px'
        }}>
          <h3 className="heading-1" style={{ 
            marginBottom: '20px',
            color: 'var(--brand-primary)'
          }}>
            Your Industry. Our Intelligence.
          </h3>
          <p className="body-large" style={{ 
            maxWidth: '700px',
            margin: '0 auto 32px auto',
            lineHeight: 1.6
          }}>
            Don't see your industry? Our AI agents can be trained on any domain with the same deep expertise and compliance standards.
          </p>
          <button 
            className="btn-primary"
            onClick={() => {
              const contactElement = document.querySelector('#contact');
              if (contactElement) {
                contactElement.scrollIntoView({ 
                  behavior: 'smooth',
                  block: 'start'
                });
              }
            }}
          >
            Get Started
          </button>
        </div>
      </div>
    </section>
  );
};

export default Industries;